import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getClientPortal } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Metric } from '@/components/ui/metric';
import { StatusDot } from '@/components/ui/status-dot';
import { EmptyState } from '@/components/ui/empty-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);
export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getClientPortal(token).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen bg-paper flex items-center justify-center"><p className="text-small text-ink-muted">Loading portal…</p></div>;
  if (!data) return <div className="min-h-screen bg-paper flex items-center justify-center"><p className="text-small text-ink-muted">Portal not found</p></div>;

  const { client, freelancer, proposals, invoices, projects } = data;
  const totalOwed = invoices.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="min-h-screen bg-paper px-6 py-12 md:py-16" data-testid="client-portal-page">
      <div className="mx-auto max-w-[1000px]">
        {/* Header */}
        <div className="flex items-center gap-2">
          <img src="/logo192.png" alt="" className="h-6 w-6 rounded-md" />
          <span className="text-micro uppercase text-ink-subtle">FlowLancer Portal</span>
        </div>

        <div className="mt-10">
          {freelancer.business_name && (
            <p className="text-micro uppercase text-ink-subtle">{freelancer.business_name}</p>
          )}
          <h1 className="mt-3 font-display text-display-l text-ink">Welcome, {client.name}</h1>
          {client.company && <p className="mt-2 text-body text-ink-muted">{client.company}</p>}
        </div>

        {/* Stats */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <Metric label="Active projects" value={projects.filter(p => p.status === 'active').length} />
          <Metric label="Amount due" value={formatCurrency(totalOwed)} />
          <Metric label="Total paid" value={formatCurrency(totalPaid)} />
        </div>

        <Tabs defaultValue="invoices" className="mt-14">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="portal-invoices-tab">Invoices ({invoices.length})</TabsTrigger>
            <TabsTrigger value="proposals" data-testid="portal-proposals-tab">Proposals ({proposals.length})</TabsTrigger>
            <TabsTrigger value="projects" data-testid="portal-projects-tab">Projects ({projects.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="mt-8">
            {invoices.length === 0 ? (
              <EmptyState title="No invoices yet" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map(inv => (
                    <TableRow key={inv.id} data-testid={`portal-invoice-${inv.id}`}>
                      <TableCell className="font-mono text-ink">#{inv.invoice_number}</TableCell>
                      <TableCell className="text-ink-muted">
                        {inv.due_date
                          ? `Due ${new Date(inv.due_date).toLocaleDateString()}`
                          : new Date(inv.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell><StatusDot status={inv.status} /></TableCell>
                      <TableCell className="text-right text-ink">{formatCurrency(inv.total)}</TableCell>
                      <TableCell className="text-right">
                        {(inv.status === 'sent' || inv.status === 'overdue') && inv.token && (
                          <Button size="sm" asChild data-testid={`pay-invoice-${inv.id}`}>
                            <a href={`/pay/${inv.token}`}>Pay now</a>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="proposals" className="mt-8">
            {proposals.length === 0 ? (
              <EmptyState title="No proposals yet" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Proposal</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {proposals.map(p => (
                    <TableRow key={p.id} data-testid={`portal-proposal-${p.id}`}>
                      <TableCell className="text-ink">{p.title}</TableCell>
                      <TableCell className="text-ink-muted">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><StatusDot status={p.status} /></TableCell>
                      <TableCell className="text-right text-ink">{formatCurrency(p.total_amount)}</TableCell>
                      <TableCell className="text-right">
                        {p.status === 'sent' && p.token && (
                          <a href={`/p/${p.token}`} className="text-small text-forest hover:text-forest-hover">View</a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="projects" className="mt-8">
            {projects.length === 0 ? (
              <EmptyState title="No projects yet" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map(proj => (
                    <TableRow key={proj.id}>
                      <TableCell className="text-ink">{proj.name}</TableCell>
                      <TableCell className="text-ink-muted">{new Date(proj.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><StatusDot status={proj.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="mt-16 border-t border-line pt-6">
          <p className="text-small text-ink-subtle">
            {freelancer.white_label
              ? (freelancer.business_name || freelancer.name)
              : 'Powered by FlowLancer'}
            {freelancer.email ? ` · ${freelancer.email}` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

